import { createContext, useContext, useRef, useCallback } from 'react';

type PreSaveHook = () => Promise<void>;

type PreSaveContextValue = {
  register: (hook: PreSaveHook) => () => void;
  runAll: () => Promise<void>;
};

export const PreSaveContext = createContext<PreSaveContextValue | null>(null);

export const usePreSave = () => useContext(PreSaveContext);

/** Call at the top of a component that owns the Save button. Returns a stable
 *  { register, runAll } pair to be provided via PreSaveContext.Provider. */
export const usePreSaveProvider = (): PreSaveContextValue => {
  const hooksRef = useRef<Set<PreSaveHook>>(new Set());

  const register = useCallback((hook: PreSaveHook) => {
    hooksRef.current.add(hook);
    return () => {
      hooksRef.current.delete(hook);
    };
  }, []);

  const runAll = useCallback(async () => {
    await Promise.all([...hooksRef.current].map((hook) => hook()));
  }, []);

  return { register, runAll };
};
